import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import { apiClient, useAuth } from '../App';
import { CreditCard, Shield, CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

// Initialize Stripe - using test key
const stripePromise = loadStripe('pk_test_emergent');

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#334155',
      fontFamily: '"Inter", system-ui, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#94a3b8',
      },
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
};

function SetupForm({ onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);

  useEffect(() => {
    // Get setup intent from backend
    const getSetupIntent = async () => {
      try {
        const res = await apiClient.post('/subscriptions/setup-intent');
        setClientSecret(res.data.client_secret);
      } catch (err) {
        setError('Failed to initialize payment setup. Please try again.');
        toast.error('Failed to initialize payment setup');
      }
    };
    getSetupIntent();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements || !clientSecret) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Confirm the setup intent
      const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (stripeError) {
        setError(stripeError.message);
        toast.error(stripeError.message);
        setLoading(false);
        return;
      }

      if (setupIntent.status === 'succeeded') {
        // Complete setup on backend
        await apiClient.post('/subscriptions/complete-setup', {
          payment_method_id: setupIntent.payment_method,
        });
        
        toast.success('Payment method saved! Your account is now active.');
        onSuccess();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save payment method');
      toast.error(err.response?.data?.detail || 'Failed to save payment method');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Info Box */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
        <div className="flex gap-3">
          <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-green-800">Free to Join!</h4>
            <p className="text-sm text-green-700 mt-1">
              Your card will only be charged $5.00 when you accept or get a job accepted. 
              No monthly fees, no hidden charges.
            </p>
          </div>
        </div>
      </div>

      {/* Card Input */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Card Details
        </label>
        <div className="border border-slate-200 rounded-lg p-4 bg-white focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-orange-500">
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Security Notice */}
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Shield className="h-4 w-4" />
        <span>Your card information is encrypted and secure</span>
      </div>

      {/* Buttons */}
      <div className="flex gap-3">
        <Button
          type="submit"
          disabled={!stripe || loading || !clientSecret}
          className="btn-accent flex-1"
          data-testid="save-payment-btn"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <CreditCard className="h-4 w-4 mr-2" />
              Save Card &amp; Continue
            </>
          )}
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}

export default function PaymentSetup({ onSuccess, onCancel }) {
  return (
    <Elements stripe={stripePromise}>
      <SetupForm onSuccess={onSuccess} onCancel={onCancel} />
    </Elements>
  );
}
